import { supabase } from './client';

export interface Trip {
  id: string;
  title: string;
  client_ids: string[]; // Armazenado como array no PostgreSQL
  destination: string[] | string; // Pode ser string ou array de strings
  start_date?: string | null;
  end_date?: string | null;
  status: 'quotation' | 'itinerary' | 'day-by-day';
  notes?: string;
  cover_images?: string[]; // Armazenado como array no PostgreSQL
  days?: any[]; // Armazenado como JSONB no PostgreSQL
  itinerary_type?: string;
  created_at?: string;
  updated_at?: string;
}

export const tripService = {
  // Buscar todas as viagens
  getTrips: async (): Promise<Trip[]> => {
    const { data, error } = await supabase
      .from('trips')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error("Erro ao buscar viagens:", error.message);
      throw error;
    }
    
    return data || [];
  },
  
  // Buscar viagem por ID
  getTripById: async (id: string): Promise<Trip | null> => {
    const { data, error } = await supabase
      .from('trips')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      console.error(`Erro ao buscar viagem ${id}:`, error.message);
      return null;
    }
    
    return data;
  },
  
  // Buscar viagens por cliente
  getTripsByClient: async (clientId: string): Promise<Trip[]> => {
    const { data, error } = await supabase
      .from('trips')
      .select('*')
      .contains('client_ids', [clientId])
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error(`Erro ao buscar viagens do cliente ${clientId}:`, error.message);
      throw error;
    }
    
    return data || [];
  },
  
  // Criar uma nova viagem
  createTrip: async (trip: Omit<Trip, 'id' | 'created_at' | 'updated_at'>): Promise<Trip> => {
    // Gerar um UUID para a viagem
    const id = crypto.randomUUID();
    
    // Garantir que client_ids seja um array
    const safeTrip = {
      id,
      ...trip,
      client_ids: Array.isArray(trip.client_ids) ? trip.client_ids : [],
      // Garantir que destination seja formatado corretamente
      destination: Array.isArray(trip.destination) 
        ? trip.destination 
        : typeof trip.destination === 'string'
          ? trip.destination.split(',').map((d: string) => d.trim()).filter(Boolean)
          : [],
    };
    
    const { data, error } = await supabase
      .from('trips')
      .insert([safeTrip])
      .select()
      .single();
    
    if (error) {
      console.error("Erro ao criar viagem:", error.message);
      throw error;
    }
    
    return data;
  },
  
  // Atualizar viagem existente
  updateTrip: async (id: string, updates: Partial<Omit<Trip, 'id' | 'created_at' | 'updated_at'>>): Promise<Trip> => {
    // Garantir que client_ids, se presente, seja um array
    const safeUpdates: any = { ...updates };
    
    if (updates.client_ids !== undefined) {
      safeUpdates.client_ids = Array.isArray(updates.client_ids) ? updates.client_ids : [];
    }
    
    // Garantir que destination seja formatado corretamente, se presente
    if (updates.destination !== undefined) {
      safeUpdates.destination = Array.isArray(updates.destination) 
        ? updates.destination 
        : typeof updates.destination === 'string'
          ? updates.destination.split(',').map((d: string) => d.trim()).filter(Boolean)
          : [];
    }
    
    const { data, error } = await supabase
      .from('trips')
      .update({
        ...safeUpdates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error(`Erro ao atualizar viagem ${id}:`, error.message);
      throw error;
    }
    
    return data;
  },
  
  // Excluir viagem
  deleteTrip: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from('trips')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error(`Erro ao excluir viagem ${id}:`, error.message);
      throw error;
    }
    
    return true;
  },
  
  // Migrar viagens do localStorage para o Supabase
  migrateLocalTrips: async (): Promise<boolean> => {
    try {
      // Obter dados do localStorage
      const localData = localStorage.getItem('voah_trips');
      if (!localData) {
        console.log('Nenhum dado de viagens para migrar.');
        return false;
      }
      
      const trips = JSON.parse(localData);
      if (!Array.isArray(trips) || trips.length === 0) {
        console.log('Sem viagens para migrar ou formato inválido.');
        return false;
      }
      
      // Formatar dados para inserção
      const formattedTrips = trips.map(trip => ({
        id: trip.id, // Preservar IDs originais
        title: trip.title || '',
        client_ids: Array.isArray(trip.clientIds) ? trip.clientIds : [],
        destination: Array.isArray(trip.destination) 
          ? trip.destination 
          : typeof trip.destination === 'string'
            ? trip.destination.split(',').map((d: string) => d.trim()).filter(Boolean)
            : [],
        start_date: trip.startDate ? new Date(trip.startDate).toISOString() : null,
        end_date: trip.endDate ? new Date(trip.endDate).toISOString() : null,
        status: trip.status || 'quotation',
        notes: trip.notes || '',
        cover_images: Array.isArray(trip.coverImages) ? trip.coverImages : [],
        days: trip.days || [],
        itinerary_type: trip.itineraryType || '',
      }));
      
      // Inserir em lote no Supabase
      const { error } = await supabase
        .from('trips')
        .upsert(formattedTrips, { 
          onConflict: 'id',
          ignoreDuplicates: false 
        });
      
      if (error) {
        console.error("Erro ao migrar viagens:", error.message);
        throw error;
      }
      
      console.log(`${formattedTrips.length} viagens migradas com sucesso.`);
      return true;
    } catch (error) {
      console.error("Erro durante migração de viagens:", error);
      throw error;
    }
  }
}; 